import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutlookStore, useResourceStore, useSettingStore, useThemeStore } from "@shared/stores";
import HStack from "@components/layout/HStack";
import TitleSetting from "@components/ui/TitleSetting";
import Divider from "@components/ui/Divider";
import Select from "@components/ui/Select";
import Accordion from "@components/ui/Accordion";
import { settingLabels } from "@shared/constants/setting";
import { useCreateOrganizerSetting } from "@features/settings/services/useCreateOrganizerSetting";
import {
  DefaultResourceTypeEntity,
  OrganizerSettingEntity,
} from "@features/settings/services/entities/organizerSettingEntity";
import { useGlobalContext } from "@shared/contexts/GlobalContext";
import { useAppConfigStore } from "@shared/stores/appConfigStore";
import { ResourceTypeEntity } from "@features/search/services/entities/resourceTypeEntity";
import { CityIcon } from "@/pages/taskpane/components/icons/IconCustom";
import { filterResourceDefault } from "@/pages/taskpane/shared/constants/appConstants";

const DefaultResourceSetting = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useThemeStore();
  const [isOpen, setIsOpen] = useState(false);

  const header = (
    <HStack justifyContent="between" className="w-full" onClick={() => setIsOpen(!isOpen)}>
      <TitleSetting icon={<CityIcon isDarkMode={isDarkMode} />} label={t(settingLabels.defaultResourceType)} />
    </HStack>
  );

  return (
    <Accordion header={header} isOpen={isOpen} onToggleOpen={() => setIsOpen(!isOpen)}>
      {isOpen ? <DefaultResourceContent /> : null}
    </Accordion>
  );
};

export default DefaultResourceSetting;

// CONTENT

const DefaultResourceContent = () => {
  const { t } = useTranslation();
  const { setIsLoadingOverlay } = useGlobalContext();
  // const showToast = useToastStore((state) => state.showToast);
  const { organizer } = useOutlookStore();
  const { updateDefaultResourceType, defaultResourceType } = useSettingStore();
  const { resourceTypes } = useAppConfigStore();
  const { filterResource, setFilterResource } = useResourceStore();
  // const queryClient = useQueryClient();

  const resourceTypeOptions = useMemo(() => {
    return (
      (resourceTypes || [])?.map((i: ResourceTypeEntity) => ({
        label: i.displayName,
        value: i.type,
      })) || []
    );
  }, [resourceTypes]);

  const { mutate: updateDefaultResourceTypeMutation } = useCreateOrganizerSetting({
    mutationConfig: {
      onSuccess: (data: OrganizerSettingEntity) => {
        updateDefaultResourceType(data.defaultResourceType);
        // showToast(t("Resource type changed"), true);
        setFilterResource({
          ...(filterResource || filterResourceDefault),
          resourceType: data?.defaultResourceType || "",
          locations:
            data?.defaultLocations?.defaultResourceTypes
              ?.find((i: DefaultResourceTypeEntity) => i.type === data?.defaultResourceType)
              ?.locationIds?.split(",")
              .map((i) => parseInt(i)) || [],
        });
        // queryClient.setQueryData([QUERY_KEY.GET_ORGANIZER_SETTING, organizer], (oldData: OrganizerSettingEntity) => {
        //   return {
        //     ...oldData,
        //     defaultResourceType: data.defaultResourceType,
        //   };
        // });
      },
      onError: () => {
        // showToast(t("Resource type change failed"), false);
      },
      onSettled: () => {
        setIsLoadingOverlay(false);
      },
    },
  });

  const handleChangeResourceType = useCallback(
    (value: string) => {
      if (!organizer) return;
      setIsLoadingOverlay(true);
      updateDefaultResourceTypeMutation({
        organizerEmail: organizer,
        defaultResourceType: value,
      });
    },
    [organizer, updateDefaultResourceTypeMutation]
  );

  return (
    <div className="w-full">
      <Divider className="my-3" />
      <Select
        placeholder={t("Default Resource Type")}
        className="w-full h-[32px] text-sm"
        options={resourceTypeOptions}
        value={defaultResourceType}
        onChange={(value) => handleChangeResourceType(value as string)}
      />
    </div>
  );
};
